import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AccountSecurity } from "@/components/AccountSecurity";
import { PasskeyPanel } from "@/components/PasskeyPanel";
import { DeleteLoginButton } from "@/components/DeleteLoginButton";
import { PushToggle } from "@/components/PushToggle";
import { Button } from "@/components/ui/button";

/** Settings for a chauffeur or a client — password, notifications and account removal live here,
 *  off the main screen. */
export default function PortalAccount({ role }) {
  const { user, company } = useAuth();
  const navigate = useNavigate();
  const home = role === "driver" ? "/portal/driver" : "/portal/client";

  return (
    <div data-testid="portal-account" className="min-h-screen bg-ink-900 text-slate-200">
      <header className="glass-header sticky top-0 z-40 border-b border-ink-600">
        <div className="mx-auto flex h-16 max-w-2xl items-center justify-between gap-2 px-4">
          <div className="min-w-0">
            <p className="truncate font-display text-base font-semibold text-slate-100">{company?.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-gold">Your account</p>
          </div>
          <Button data-testid="portal-account-back" variant="ghost" size="sm"
            onClick={() => navigate(home)} className="shrink-0 text-slate-400 hover:text-gold">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-6 px-4 py-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-50">{user?.name}</h1>
          <p className="mt-1.5 text-sm text-slate-400">{user?.email}</p>
        </div>

        <PushToggle
          scope={role}
          description={role === "driver"
            ? "Get told the moment a new job is offered to you, even when the app is closed."
            : "Get told when your chauffeur is on the way and when they arrive."} />

        <div className="rounded-xl border border-ink-600 bg-ink-800/60 p-5">
          <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Face ID unlock</p>
          <div className="mt-3">
            <PasskeyPanel testId="portal-passkeys" />
          </div>
        </div>

        <AccountSecurity />

        <DeleteLoginButton
          redirectTo={role === "driver" ? "/drivers" : "/clients"}
          note={role === "driver"
            ? "Removes your chauffeur login. Your completed trips and payout records stay with the company for their books."
            : "Removes your passenger login and sign-in links. Your operator keeps the trip and invoice records for their own books, as they are required to."} />
      </main>
    </div>
  );
}
